<script setup lang="ts">
// "16 раскладов" — every spread as a patch of the app's patchwork (the /spread tiles:
// the same stock colour, fabric and drawing per spread), linking to its SEO page.
// Spreads without a drawing show a fan of face-down cards, one per card in the reading.
import { RouterLink } from 'vue-router';
import { Sparkles, Lock } from '@lucide/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import CardFan from '@app/presentation/components/paper/CardFan.vue';
import type { CutoutName } from '@app/utils/paperCutouts';
import { SPREAD_SEO_DATA } from '@app/data/spreadSeoData';

// All 16 spreads, static at setup scope so vite-ssg emits every
// <a href="/rasklady/<slug>"> into the pre-rendered homepage HTML —
// this de-orphans the 16 detail pages (internal-linking SEO).
const spreads = Object.values(SPREAD_SEO_DATA);

const cardWord = (n: number) => (n === 1 ? 'карта' : n <= 4 ? 'карты' : 'карт');

interface Patch {
  stock: string;
  fabric: string;
  /** Her drawing: width (% of the patch), `tuck` = a cropped drawing's flat edge(s) hidden past the patch edge. */
  art?: { cutout: CutoutName; width: number; tuck?: 'bottom' | 'bottom-right'; still?: boolean };
}

// Keyed by spread id — the same patch each spread has in the app (/spread, Spread.vue).
const PATCHES: Record<string, Patch> = {
  daily: { stock: '', fabric: 'fabric-velvet' },
  'yes-no': { stock: 'bg-stock-wine', fabric: 'fabric-quilted', art: { cutout: 'wizard_cat', width: 70, tuck: 'bottom' } },
  'question-single': { stock: 'bg-stock-plum', fabric: 'fabric-knit', art: { cutout: 'paw_prints', width: 78, still: true } },
  'question-three': { stock: 'bg-stock-tide', fabric: 'fabric-denim' },
  'a-or-b': { stock: 'bg-stock-bronze', fabric: 'fabric-corduroy', art: { cutout: 'card_paws', width: 58, tuck: 'bottom' } },
  love: { stock: 'bg-stock-rust', fabric: 'fabric-gingham', art: { cutout: 'love_cat', width: 52 } },
  financial: { stock: 'bg-stock-sage', fabric: 'fabric-herringbone', art: { cutout: 'rich_cat', width: 64, tuck: 'bottom' } },
  destiny: { stock: 'bg-stock-oxblood', fabric: 'fabric-celestial', art: { cutout: 'peek_cat', width: 70, tuck: 'bottom-right' } },
  education: { stock: 'bg-stock-cobalt', fabric: 'fabric-tartan' },
  'ex-partner': { stock: 'bg-stock-rose', fabric: 'fabric-dots' },
  disappearance: { stock: 'bg-stock-lilac', fabric: 'fabric-linen' },
  'wait-or-release': { stock: 'bg-stock-mauve', fabric: 'fabric-seersucker' },
  situation: { stock: 'bg-stock-moss', fabric: 'fabric-waffle' },
  'career-crossroads': { stock: 'bg-stock-fern', fabric: 'fabric-basket' },
  talent: { stock: 'bg-stock-ochre', fabric: 'fabric-fairisle' },
  'self-discovery': { stock: 'bg-stock-slate', fabric: 'fabric-pinstripe' },
};
const patchOf = (id: string): Patch => PATCHES[id] ?? { stock: 'bg-stock-plum', fabric: 'fabric-linen' };

// Tucked drawings push their cut edge just past the patch edge (the patch clips it).
const artStyle = (art: NonNullable<Patch['art']>) => {
  const style: Record<string, string> = { width: `${art.width}%` };
  if (art.tuck === 'bottom-right') return { ...style, right: '-7%', bottom: '-7%' };
  return { ...style, right: '7%', bottom: art.tuck === 'bottom' ? '-5%' : '10px' };
};
</script>

<template>
  <section
    id="spreads"
    class="scroll-mt-20 py-16 md:py-24"
  >
    <div class="site-container">
      <ScrollReveal class="mb-8 flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <div class="type-overline mb-3.5 text-primary-ink">
            Колода на любой случай
          </div>
          <h2 class="type-display">16 раскладов</h2>
        </div>
        <RouterLink
          to="/rasklady"
          class="type-body-strong shrink-0 text-primary-ink underline-offset-4 hover:underline"
        >Все расклады →</RouterLink>
      </ScrollReveal>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
        <ScrollReveal
          v-for="(s, i) in spreads"
          :key="s.slug"
          :delay="((i % 4) + 1) * 60"
        >
          <RouterLink
            :to="`/rasklady/${s.slug}`"
            class="spread-patch paper-sheet relative flex h-full min-h-[208px] flex-col overflow-hidden text-foreground"
            :class="[patchOf(s.id).stock, patchOf(s.id).fabric]"
          >
            <span class="relative z-[1] block p-4 md:p-5">
              <span
                class="patch-tag inline-flex w-fit items-center gap-1 px-2 py-0.5 text-[10px] font-semibold"
                :class="s.free ? 'paper-tag' : 'is-sub'"
              >
                <Sparkles
                  v-if="s.free"
                  class="h-2.5 w-2.5"
                  aria-hidden="true"
                />
                <Lock
                  v-else
                  class="h-2.5 w-2.5"
                  aria-hidden="true"
                />
                {{ s.free ? 'Бесплатно' : 'Подписка' }}
              </span>
              <span class="type-app-heading mt-3 block">{{ s.title }}</span>
              <span class="patch-more mt-1 block text-[11px] opacity-85">
                {{ s.cardCount }} {{ cardWord(s.cardCount) }} →
              </span>
            </span>

            <!-- Art: her drawing on the patch's bottom edge, or a fan of face-down cards. -->
            <span
              class="relative z-[1] block min-h-[92px] flex-1"
              aria-hidden="true"
            >
              <span
                v-if="patchOf(s.id).art"
                class="absolute block"
                :style="artStyle(patchOf(s.id).art!)"
              >
                <PaperCutout
                  :name="patchOf(s.id).art!.cutout"
                  lite
                  lazy
                  :still="patchOf(s.id).art!.still"
                />
              </span>
              <span
                v-else
                class="absolute -bottom-1 right-3 block"
              >
                <CardFan
                  :count="s.cardCount"
                  :card-width="40"
                />
              </span>
            </span>
          </RouterLink>
        </ScrollReveal>
      </div>
    </div>
  </section>
</template>

<style scoped>
.spread-patch {
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}
.spread-patch:hover {
  transform: translateY(-4px);
}
.spread-patch:active {
  transform: translateY(1px);
}
.spread-patch:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 2px;
}
.patch-tag {
  border-radius: var(--paper-radius-xs);
  transform: rotate(-2deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.4), 0 2px 4px -1px var(--paper-shade);
}
/* Subscription: a chip of the page's dark stock. */
.patch-tag.is-sub {
  background: var(--background);
  color: var(--foreground);
  box-shadow: inset 0 1px 0 var(--paper-edge), 0 2px 4px -1px var(--paper-shade);
}
.spread-patch:hover .patch-more {
  transform: translateX(4px);
}
.patch-more {
  transition: transform 0.2s ease-out;
}
@media (prefers-reduced-motion: reduce) {
  .spread-patch,
  .patch-more {
    transition: none;
  }
}
</style>
